import { AxiosInstance } from 'axios';
import { ListTeamsRequest, ListTeamsResponse, SportsMetadataResponse } from '../types/sports';
import { buildOffsetParams } from '../util/pagination';
import {
  ListTeamsRequestSchema,
  ListTeamsResponseSchema,
  SportsMetadataResponseSchema,
} from '../z/sports';

export class SportsClient {
  constructor(private readonly http: AxiosInstance) {}

  /**
   * List all sports teams with optional filtering and pagination.
   *
   * This endpoint returns a paginated list of sports teams with optional filtering
   * by sport and league. Useful for building team selection interfaces or
   * filtering teams by specific sports categories.
   *
   * @example
   * ```typescript
   * // Get all basketball teams
   * const teams = await client.sports.listTeams({
   *   sport: 'basketball',
   *   limit: 10
   * });
   *
   * // Get teams from a specific league
   * const nbaTeams = await client.sports.listTeams({
   *   sport: 'basketball',
   *   league: 'NBA',
   *   limit: 30
   * });
   * ```
   *
   * @param params - Optional query parameters for filtering and pagination
   * @param params.limit - Number of teams to return (default: 10)
   * @param params.offset - Number of teams to skip (default: 0)
   * @param params.sport - Filter by sport (e.g., 'basketball', 'football')
   * @param params.league - Filter by league (e.g., 'NBA', 'NFL')
   *
   * @returns Promise<ListTeamsResponse> - Paginated list of teams
   * @throws {Error} When the API request fails or returns invalid data
   *
   * @see {@link https://docs.polymarket.com/api-reference/sports/list-teams} - API Documentation
   */
  async listTeams(params?: ListTeamsRequest): Promise<ListTeamsResponse> {
    const validatedParams = params ? ListTeamsRequestSchema.parse(params) : {};
    const queryParams = buildOffsetParams(validatedParams);

    try {
      const response = await this.http.get('/sports/teams', { params: queryParams });
      const validatedData = ListTeamsResponseSchema.parse(response.data);

      return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to list teams: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * Get sports metadata information including available sports and leagues.
   *
   * This endpoint returns comprehensive metadata about all available sports
   * and their associated leagues. Useful for building dynamic sport/league
   * selection interfaces or understanding the available sports categories.
   *
   * @example
   * ```typescript
   * const metadata = await client.sports.getMetadata();
   * console.log('Available sports:', metadata.sports.map(s => s.name));
   *
   * // Find basketball leagues
   * const basketball = metadata.sports.find(s => s.name === 'Basketball');
   * console.log('Basketball leagues:', basketball?.leagues?.map(l => l.name));
   * ```
   *
   * @returns Promise<SportsMetadataResponse> - Sports metadata containing sports and leagues
   * @throws {Error} When the API request fails or returns invalid data
   *
   * @see {@link https://docs.polymarket.com/api-reference/sports/get-sports-metadata-information} - API Documentation
   */
  async getMetadata(): Promise<SportsMetadataResponse> {
    try {
      const response = await this.http.get('/sports/metadata');
      const validatedData = SportsMetadataResponseSchema.parse(response.data);

      return validatedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to get sports metadata: ${error.message}`);
      }
      throw error;
    }
  }
}
